// DKM-5486 Cedric: telefon numarası yapıştırma/kopyalama için ortak yardımcılar.
// WhatsApp, e-posta vb. yerden gelen "0532 123 45 67", "+90 532 123 4567", "0090 532 1234567",
// "5321234567" gibi tam numaralar maskeli/ikiye bölünmüş telefon alanlarına tek seferde yapıştırılabilsin diye.
// dk-phone (alan kodu + numara iki kutu) ve tek kutulu maskeli telefon alanları bu yardımcıları kullanır.

/**
 * DKM-5486 Cedric (28.10, dtokgoz: "başında 0 olursa onu ihmal et, yapıştırma devam etsin"):
 * Metni rakamlara indirger; ülke kodunu (+90 / 0090 / 12 haneli 90…) ve BAŞTAKİ TÜM 0'ları atar.
 * Eksik numarada da çalışır: "0532" → "532", "0532 123" → "532123", "+90 0532 123 45 67" → "5321234567".
 */
export function telefonRakamlari(metin: string): string {
  const ham = String(metin ?? '').trim();
  let rakam = ham.replace(/\D/g, '');
  if (rakam.startsWith('0090')) rakam = rakam.substring(4);
  else if (/^\(?\+\s*90/.test(ham) || (rakam.length === 12 && rakam.startsWith('90'))) rakam = rakam.substring(2);
  return rakam.replace(/^0+/, '');
}

/** Tam numarayı ulusal 10 haneye indirger (başındaki 0'lar / 90 / 0090 atılır). Tam numara değilse null. */
export function telefonUlusal(metin: string): string | null {
  // DKM-5486 Cedric (28.10): yalnız tek 0 / tam uzunlukta önek atılıyordu ("00532…", "+90 0532…" null dönüyordu); eski hâli:
  // let rakam = (metin || '').replace(/\D/g, '');
  // if (rakam.length === 14 && rakam.startsWith('0090')) rakam = rakam.substring(4);
  // else if (rakam.length === 12 && rakam.startsWith('90')) rakam = rakam.substring(2);
  // else if (rakam.length === 11 && rakam.startsWith('0')) rakam = rakam.substring(1);
  // return rakam.length === 10 ? rakam : null;
  const rakam = telefonRakamlari(metin);
  return rakam.length === 10 ? rakam : null;
}

/**
 * DKM-5486 Cedric (28.10): eksik numara yapıştırmada (tam numara değil) baştaki 0 / ülke kodu atıldıktan sonra
 * kalan rakamlar. Metinde atılacak bir önek yoksa null (varsayılan yapıştırma sürer). Yalnız 0'lardan oluşuyorsa ''.
 */
export function telefonOnekAtilmis(metin: string): string | null {
  const ham = String(metin ?? '').replace(/\D/g, '');
  if (!ham) return null;
  const rakam = telefonRakamlari(metin);
  return rakam !== ham && rakam.length < 10 ? rakam : null;
}

/**
 * DKM-5486 Cedric (28.10): elle yazmada alan kodunun ilk hanesine yazılan 0 yok sayılsın diye
 * maskeli kutuda kullanıcının girdiği rakamlar (maskedeki kaçışlı sabit rakamlar — `+\9\0` — hariç).
 * Kutunun tamamı seçiliyse yazılan karakter her şeyi değiştireceği için ''.
 */
export function telefonGirilenRakam(input: HTMLInputElement, mask?: string): string {
  const deger = input?.value || '';
  const bas = input?.selectionStart ?? deger.length;
  const son = input?.selectionEnd ?? deger.length;
  if (son > bas && bas === 0 && son === deger.length) return '';
  const sabit = (String(mask || '').match(/\\\d/g) || []).length;
  return deger.replace(/\D/g, '').substring(sabit);
}

/** Tam numarayı alan kodu (3 hane) + numara (7 hane) olarak böler. Tam numara değilse null. */
export function telefonParcala(metin: string): { alan: string; tel: string } | null {
  const ulusal = telefonUlusal(metin);
  if (!ulusal) return null;
  return { alan: ulusal.substring(0, 3), tel: ulusal.substring(3) };
}

/** Alan kodu + numarayı panoya uygun tek parça yapar (05321234567). Eksikse null. */
export function telefonBirlestir(alan: any, tel: any): string | null {
  const a = String(alan ?? '').replace(/\D/g, '');
  const t = String(tel ?? '').replace(/\D/g, '');
  if (!a || !t) return null;
  return '0' + a + t;
}

/**
 * Maskeli telefon alanı için editorOptions üretir (mask + tam numara yapıştırma).
 * Örn. bileşende: `gsmEditorOptions = telefonMaskeOptions('(000) 000 00 00');`
 * @param ek diğer editorOptions (placeholder, maskChar …); kendi onInitialized'i varsa o da çağrılır.
 */
export function telefonMaskeOptions(mask: string, onek = '', ek: Record<string, any> = {}): Record<string, any> {
  const { onInitialized, ...digerleri } = ek || {};
  return {
    ...digerleri,
    mask,
    onInitialized: (e: any) => {
      telefonYapistirBagla(e, onek);
      onInitialized?.(e);
    },
  };
}

/**
 * Tek kutulu maskeli telefon alanına (dx-text-box) tam numara yapıştırmayı bağlar.
 * DevExtreme maskesi yapıştırılan metni baştan sırayla yerleştirdiği için "0532..." ya da "+90 532..."
 * yapıştırınca numara kayıyordu. Dinleyici widget'ın kök elemanına capture aşamasında eklenir
 * (maske işleyicisinden önce); metin tam numaraysa değer `onek + 10 hane` olarak option('value') ile yazılır.
 * Tam numara değilse hiçbir şey yapmaz (varsayılan yapıştırma).
 *
 * Kullanım (dxi-item editorOptions ya da dx-text-box):
 *   onInitialized: (e) => telefonYapistirBagla(e)            // maske 10 hane: (000) 000 00 00
 *   onInitialized: (e) => telefonYapistirBagla(e, '0')       // maske 11 hane: 0 (000) 000 00 00
 *
 * @param e DevExtreme onInitialized olayı ({ component, element })
 * @param onek değerin başına eklenecek rakamlar (maske 10 haneden uzunsa)
 */
export function telefonYapistirBagla(
  e: { component?: any; element?: any },
  onek = ''
): void {
  const el: HTMLElement = e?.element?.nodeType ? e.element : e?.element?.[0];
  const bilesen = e?.component;
  if (!el || !bilesen || (el as any).__dkTelYapistir) return;
  (el as any).__dkTelYapistir = true;
  el.addEventListener(
    'paste',
    (ev: ClipboardEvent) => {
      if (bilesen.option?.('readOnly') || bilesen.option?.('disabled')) return;
      // DKM-5486 Cedric (28.10): eksik numarada da baştaki 0 / ülke kodu atılıp yapıştırma sürsün; eski hâli:
      // const ulusal = telefonUlusal(ev?.clipboardData?.getData('text') ?? '');
      // if (!ulusal) return;
      // ev.preventDefault();
      // ev.stopPropagation(); // maske işleyicisine ulaşmasın
      // setTimeout(() => bilesen.option('value', onek + ulusal));
      const metin = ev?.clipboardData?.getData('text') ?? '';
      const rakam = telefonUlusal(metin) ?? telefonOnekAtilmis(metin);
      if (rakam === null) return; // önek yok, eksik numara: varsayılan yapıştırma
      ev.preventDefault();
      ev.stopPropagation(); // maske işleyicisine ulaşmasın
      if (!rakam) return; // yalnız "0" yapıştırıldı: yok sayılır
      setTimeout(() => bilesen.option('value', onek + rakam));
    },
    true
  );
  // DKM-5486 Cedric (28.10): elle yazmada alan kodunun ilk hanesine 0 yazılırsa yok sayılır
  // ("0532…" yazan kullanıcıda numara kaymasın). Önekli maskede (0 (000)…, +90(000)…) önek yazıldıktan sonraki 0.
  el.addEventListener(
    'keydown',
    (ev: KeyboardEvent) => {
      if (ev.key !== '0' || ev.ctrlKey || ev.altKey || ev.metaKey) return;
      if (bilesen.option?.('readOnly') || bilesen.option?.('disabled')) return;
      const input = ev.target as HTMLInputElement;
      if (!input || typeof input.selectionStart !== 'number') return;
      if (telefonGirilenRakam(input, bilesen.option?.('mask')) === onek) ev.preventDefault();
    },
    true
  );
}

/**
 * Alan kodu ile numaranın AYRI editörlerde tutulduğu formlar (dk-phone kullanmayan dx-form çiftleri) için
 * tam numara yapıştırma + kopyalama. Editörün onInitialized olayında çağrılır (editorOptions.onInitialized).
 * - Yapıştırma: metin tam numaraysa (0532 123 45 67, +90 532…, 5321234567) varsayılan yapıştırma engellenir
 *   ve `yaz(alan, tel)` çağrılır (alan '532', tel '1234567'; değerleri ekran kendi tipine göre yazar).
 * - Kopyalama: seçim yokken ya da kutunun tamamı seçiliyken panoya tam numara (05321234567) konur.
 *   Kısmi seçimde varsayılan kopyalama.
 * Dinleyiciler widget'ın kök elemanına (e.element) eklenir; DevExtreme iç DOM'u değiştirilmez.
 */
export function telefonCiftBagla(
  e: { component?: any; element?: any },
  ayar: { yaz: (alan: string, tel: string) => void; oku?: () => { alan: any; tel: any } }
): void {
  const el: HTMLElement = e?.element?.nodeType ? e.element : e?.element?.[0];
  const bilesen = e?.component;
  if (!el || !bilesen || (el as any).__dkTelCift) return;
  (el as any).__dkTelCift = true;
  el.addEventListener(
    'paste',
    (ev: ClipboardEvent) => {
      if (bilesen.option?.('readOnly') || bilesen.option?.('disabled')) return;
      const parca = telefonParcala(ev?.clipboardData?.getData('text') ?? '');
      if (!parca) return;
      ev.preventDefault();
      ev.stopPropagation(); // maske/sayı işleyicisine ulaşmasın
      setTimeout(() => ayar.yaz(parca.alan, parca.tel));
    },
    true
  );
  if (!ayar.oku) return;
  el.addEventListener('copy', (ev: ClipboardEvent) => {
    const input = ev?.target as HTMLInputElement;
    if (!ev?.clipboardData || !input || typeof input.selectionStart !== 'number') return;
    const secimVar = input.selectionStart !== input.selectionEnd;
    const tamSecim = input.selectionStart === 0 && input.selectionEnd === (input.value || '').length;
    if (secimVar && !tamSecim) return;
    const deger = ayar.oku();
    const tam = telefonBirlestir(deger?.alan, deger?.tel);
    if (!tam) return;
    ev.clipboardData.setData('text/plain', tam);
    ev.preventDefault();
  });
}
